/**
 * Driver Service - Frontend API calls for driver operations
 */

import api from "@/shared/api";
import { logError, logInfo } from "@/shared/utils/logger.js";

/**
 * Get drivers with optional filters
 * @param {Object} filters - Filter criteria
 * @param {string} filters.ownerType - Owner type (bubakii, third_party)
 * @param {string} filters.partnerId - Partner ID
 * @param {boolean} filters.isActive - Active status
 * @returns {Promise<Object>} Drivers list
 */
const buildDriverQueryParams = (filters = {}) => {
  const params = {};
  if (filters.ownerType) params.ownerType = filters.ownerType;
  if (filters.partnerId) params.partnerId = filters.partnerId;
  if (filters.isActive === true || filters.isActive === false) {
    params.isActive = filters.isActive;
  }
  return params;
};

export const getDrivers = async (filters = {}) => {
  try {
    const response = await api.get("/drivers", {
      params: buildDriverQueryParams(filters),
    });
    logInfo("Drivers fetched successfully", { filters, count: response?.data?.length || 0 });
    return response;
  } catch (error) {
    logError("Failed to fetch drivers", {
      filters,
      error: error.message,
    });
    return {
      success: false,
      error: error.message || "Failed to fetch drivers",
    };
  }
};

/**
 * Get driver by ID
 * @param {string} driverId - Driver ID
 * @returns {Promise<Object>} Driver details
 */
export const getDriver = async (driverId) => {
  try {
    const response = await api.get(`/drivers/${driverId}`);
    logInfo("Driver fetched successfully", { driverId });
    return response;
  } catch (error) {
    logError("Failed to fetch driver", {
      driverId,
      error: error.message,
    });
    return {
      success: false,
      error: error.message || "Failed to fetch driver",
    };
  }
};

/**
 * Create driver
 * @param {Object} driverData - Driver data
 * @param {string} driverData.name - Driver name
 * @param {string} driverData.phone - Driver phone
 * @param {string} [driverData.email] - Driver email
 * @param {string} driverData.licenseNumber - License number
 * @param {Date|string} driverData.licenseExpiry - License expiry date
 * @param {string} driverData.ownerType - Owner type (bubakii, third_party)
 * @param {string} [driverData.partnerId] - Partner ID if third_party
 * @returns {Promise<Object>} Creation result
 */
export const createDriver = async (driverData) => {
  try {
    const response = await api.post("/drivers", driverData);
    logInfo("Driver created successfully", { driverId: response?.id || response?.data?.id });
    return response;
  } catch (error) {
    logError("Failed to create driver", {
      driverData,
      error: error.message,
    });
    return {
      success: false,
      error: error.message || "Failed to create driver",
    };
  }
};

/**
 * Update driver
 * @param {string} driverId - Driver ID
 * @param {Object} updates - Update data
 * @returns {Promise<Object>} Update result
 */
export const updateDriver = async (driverId, updates) => {
  try {
    const response = await api.put(`/drivers/${driverId}`, updates);
    logInfo("Driver updated successfully", { driverId });
    return response;
  } catch (error) {
    logError("Failed to update driver", {
      driverId,
      updates,
      error: error.message,
    });
    return {
      success: false,
      error: error.message || "Failed to update driver",
    };
  }
};

/**
 * Get available drivers for a date
 * @param {Date|string} date - Date to check availability
 * @param {Object} filters - Additional filters
 * @param {string} filters.ownerType - Owner type filter
 * @param {string} filters.partnerId - Partner ID filter
 * @returns {Promise<Object>} Available drivers
 */
export const getAvailableDrivers = async (date, filters = {}) => {
  try {
    const dateStr = date instanceof Date ? date.toISOString().split("T")[0] : date;
    const response = await api.get("/drivers/available", {
      params: { date: dateStr, ...filters },
    });
    logInfo("Available drivers fetched successfully", { date: dateStr, filters });
    return response;
  } catch (error) {
    logError("Failed to fetch available drivers", {
      date: dateStr,
      filters,
      error: error.message,
    });
    return {
      success: false,
      error: error.message || "Failed to fetch available drivers",
    };
  }
};

